import React, { useRef, useEffect, useState } from 'react'
import JsBarcode from 'jsbarcode'
import { Printer, Download, RefreshCw, X, CheckCircle2, AlertCircle } from 'lucide-react'
import { useSettingsStore } from '@/store/settingsStore'
import { buildPrintHTML } from '@/utils/printHTML'

interface BarcodeGeneratorProps {
  barcode: string
  productName?: string
  sku?: string
  shopName?: string
  onClose: () => void
  onPrint?: () => void
}

export const BarcodeGenerator: React.FC<BarcodeGeneratorProps> = ({
  barcode,
  productName,
  // sku, // Unused
  shopName: shopNameProp,
  onClose,
  onPrint
}) => {
  const settings = useSettingsStore(s => s.settings)
  const shopName = shopNameProp ?? settings?.receiptHeader ?? settings?.storeName ?? ''
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [barcodeFormat, setBarcodeFormat] = useState<'CODE128' | 'EAN13' | 'EAN8' | 'CODE39'>('CODE128')
  // Default: 30mm x 20mm sticker (in inches for legacy inputs: 30/25.4 ≈ 1.181, 20/25.4 ≈ 0.787)
  const [labelWidthMm, setLabelWidthMm] = useState(30)
  const [labelHeightMm, setLabelHeightMm] = useState(20)
  const [showProductInfo, setShowProductInfo] = useState(true)
  const [copies, setCopies] = useState(3)
  const [labelsPerRow, setLabelsPerRow] = useState(3) // 3 stickers per row
  const [barcodeScale] = useState(305) // Fixed at 250% for larger barcodes
  const [error, setError] = useState<string | null>(null)

  const isSmallLabel = labelWidthMm <= 35 && labelHeightMm <= 25

  useEffect(() => {
    if (barcode) {
      generateBarcode()
    }
  }, [barcode, barcodeFormat, barcodeScale, labelWidthMm, labelHeightMm])

  const generateBarcode = () => {
    if (!canvasRef.current || !barcode || barcode.trim() === '') {
      setError('Barcode is required to generate label')
      return
    }

    try {
      setError(null)

      // Determine format based on barcode length
      let format = barcodeFormat
      if (barcode.length === 13 && barcodeFormat === 'CODE128') {
        format = 'EAN13'
      } else if (barcode.length === 8 && barcodeFormat === 'CODE128') {
        format = 'EAN8'
      }

      // Bar width >= 2 and integer for clarity; taller bars + larger font
      const scaleFactor = barcodeScale / 120
      const baseWidth = isSmallLabel ? 1.2 : 1.4
      const baseHeight = isSmallLabel ? 26 : 32
      const baseFontSize = isSmallLabel ? 8 : 9
      const marginPx = isSmallLabel ? 4 : 5
      const barWidth = Math.max(2, Math.round(baseWidth * scaleFactor))
      const barHeight = baseHeight * scaleFactor
      const fontSize = Math.max(8, Math.round(baseFontSize * scaleFactor))

      JsBarcode(canvasRef.current, barcode, {
        format: format,
        width: barWidth,
        height: barHeight,
        displayValue: true,
        fontSize,
        textMargin: 2,
        margin: marginPx,
        background: '#ffffff',
        lineColor: '#000000',
        valid: (valid: boolean) => {
          if (!valid) {
            setError(`Invalid barcode format for ${format}. Using CODE128 instead.`)
            JsBarcode(canvasRef.current!, barcode, {
              format: 'CODE128',
              width: Math.max(2, barWidth),
              height: barHeight,
              displayValue: true,
              fontSize: Math.max(8, fontSize),
              textMargin: 2,
              margin: marginPx,
              background: '#ffffff',
              lineColor: '#000000',
            })
          }
        }
      })
    } catch (err: any) {
      console.error('Barcode generation error:', err)
      setError(err.message || 'Failed to generate barcode')
    }
  }

  const handlePrint = () => {
    if (!canvasRef.current || !barcode || barcode.trim() === '') return

    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      alert('Please allow popups to print barcode labels')
      return
    }

    // Prefer SVG for print (vector = perfectly sharp at any size); fallback to high-res canvas
    let format = barcodeFormat
    if (barcode.length === 13 && barcodeFormat === 'CODE128') format = 'EAN13'
    else if (barcode.length === 8 && barcodeFormat === 'CODE128') format = 'EAN8'
    const scaleFactor = barcodeScale / 100
    const baseWidth = isSmallLabel ? 1.2 : 1.4
    const baseHeight = isSmallLabel ? 26 : 32
    const baseFontSize = isSmallLabel ? 8 : 9
    const marginPx = isSmallLabel ? 4 : 5
    const barWidth = Math.max(2, Math.round(baseWidth * scaleFactor))
    const barHeight = baseHeight * scaleFactor
    const fontSize = Math.max(8, Math.round(baseFontSize * scaleFactor))
    const opts = {
      format,
      width: barWidth,
      height: barHeight,
      displayValue: true,
      fontSize,
      textMargin: 3,
      margin: marginPx,
      background: '#ffffff',
      lineColor: '#000000',
    }

    let barcodeDataUrl: string
    try {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      svg.setAttribute('shape-rendering', 'crispEdges')
      JsBarcode(svg, barcode, opts)
      const xml = new XMLSerializer().serializeToString(svg)
      barcodeDataUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(xml)))
    } catch {
      // Fallback: high-res canvas (12x for maximum sharpness)
      const printScale = 12
      const cw = Math.max(2, Math.round(baseWidth * scaleFactor)) * printScale
      const ch = baseHeight * scaleFactor * printScale
      const cFont = Math.max(8, Math.round(baseFontSize * scaleFactor)) * printScale
      const offscreen = document.createElement('canvas')
      try {
        JsBarcode(offscreen, barcode, {
          ...opts,
          width: cw,
          height: ch,
          fontSize: cFont,
          textMargin: 3 * printScale,
          margin: marginPx * printScale,
        })
      } catch {
        JsBarcode(offscreen, barcode, {
          ...opts,
          format: 'CODE128',
          width: cw,
          height: ch,
          fontSize: cFont,
          textMargin: 3 * printScale,
          margin: marginPx * printScale,
        })
      }
      barcodeDataUrl = offscreen.toDataURL('image/png')
    }
    console.log('Barcode data URL length:', barcodeDataUrl.length, 'Preview:', barcodeDataUrl.substring(0, 100))

    // 30mm x 20mm sticker: use mm for exact print size
    const wMm = labelWidthMm
    const hMm = labelHeightMm
    const gapMm = 15 // 15mm gap requested
    const totalWidthMm = (wMm * labelsPerRow) + (gapMm * Math.max(0, labelsPerRow - 1))
    const paddingMm = isSmallLabel ? 1 : 2
    const hasShopName = false // shopName.trim().length > 0
    const barcodeMaxHeightPct = hasShopName ? (showProductInfo ? 50 : 65) : (showProductInfo ? 58 : 75)
    const shopNameFontSizeMm = isSmallLabel ? 1.3 : 1.6
    // Make product name clearly larger than SKU and more readable above the barcode
    const productFontSizeMm = isSmallLabel ? 2.4 : 2.8
    const skuFontSizeMm = isSmallLabel ? 1.6 : 1.9

    // Build print HTML
    console.log('Print parameters:', { copies, labelsPerRow, wMm, hMm, totalWidthMm })
    const printContent = buildPrintHTML(
      wMm,
      hMm,
      totalWidthMm,
      paddingMm,
      shopNameFontSizeMm,
      productFontSizeMm,
      skuFontSizeMm,
      barcodeMaxHeightPct,
      labelsPerRow,
      barcodeDataUrl,
      copies,
      false, // hasShopName
      '', // shopName
      showProductInfo,
      productName,
      undefined // sku
    )

    const blob = new Blob([printContent], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    printWindow.location.href = url

    // Wait for images to load before printing
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print()
        if (onPrint) {
          onPrint()
        }
      }, 250)
    }
  }

  const handleDownload = () => {
    if (!canvasRef.current) return

    const canvas = canvasRef.current
    const link = document.createElement('a')
    link.download = `barcode-${barcode}-${Date.now()}.png`
    link.href = canvas.toDataURL('image/png')
    link.click()
  }

  const commonLabelSizes = [
    { w: 30, h: 20, label: '30mm × 20mm (Sticker)' },
    { w: 40, h: 30, label: '40mm × 30mm' },
    { w: 50, h: 25, label: '50mm × 25mm' },
    { w: 60, h: 30, label: '60mm × 30mm' },
  ]

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="glass-card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold esales-text">Generate Barcode</h2>
            <p className="text-xs text-muted-foreground mt-1">Create and print barcode labels</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-600 dark:text-yellow-400">{error}</p>
          </div>
        )}

        {/* Barcode Preview — aspect ratio matches label (e.g. 30×20mm) */}
        <div className="mb-6 flex flex-col items-center">
          <p className="text-xs text-muted-foreground mb-2">Preview (proportion {labelWidthMm}×{labelHeightMm}mm)</p>
          <div
            className="bg-white rounded-lg border-2 border-border overflow-hidden flex flex-col items-center justify-center"
            style={{ aspectRatio: labelWidthMm / labelHeightMm, maxWidth: '100%', width: 280, padding: 8, paddingLeft: 2 }}
          >
            <div className="flex-1 min-h-0 w-full flex flex-col items-center justify-center">

              <canvas ref={canvasRef} className="max-w-full max-h-full object-contain" style={{ maxHeight: shopName ? (showProductInfo ? '52%' : '72%') : (showProductInfo ? '62%' : '85%') }} />
              {showProductInfo && (
                <div className="mt-1 text-center w-full min-w-0">
                  {productName && (
                    <p className="font-bold text-sm truncate px-1">{productName}</p>
                  )}

                </div>
              )}
            </div>
          </div>
        </div>

        {/* Settings */}
        <div className="space-y-4 mb-6">
          {/* Barcode Format */}
          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase mb-2 block">
              Barcode Format
            </label>
            <select
              value={barcodeFormat}
              onChange={(e) => {
                setBarcodeFormat(e.target.value as any)
                generateBarcode()
              }}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg"
            >
              <option value="CODE128">CODE128 (Universal)</option>
              <option value="EAN13">EAN13 (13 digits)</option>
              <option value="EAN8">EAN8 (8 digits)</option>
              <option value="CODE39">CODE39</option>
            </select>
          </div>

          {/* Label Size (30×20mm default) */}
          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase mb-2 block">
              Label Size (mm)
            </label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              {commonLabelSizes.map((size, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setLabelWidthMm(size.w)
                    setLabelHeightMm(size.h)
                  }}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${labelWidthMm === size.w && labelHeightMm === size.h
                    ? 'bg-primary text-white'
                    : 'bg-muted hover:bg-muted/80'
                    }`}
                >
                  {size.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Width (mm)</label>
                <input
                  type="number"
                  step="1"
                  min="20"
                  max="100"
                  value={labelWidthMm}
                  onChange={(e) => setLabelWidthMm(Math.max(20, Math.min(100, parseInt(e.target.value) || 30)))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Height (mm)</label>
                <input
                  type="number"
                  step="1"
                  min="15"
                  max="80"
                  value={labelHeightMm}
                  onChange={(e) => setLabelHeightMm(Math.max(15, Math.min(80, parseInt(e.target.value) || 20)))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                />
              </div>
            </div>
          </div>



          {/* Options */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-muted-foreground uppercase mb-2 block">
                Number of Copies
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={copies}
                onChange={(e) => setCopies(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-muted-foreground uppercase mb-2 block">
                Labels Per Row
              </label>
              <input
                type="number"
                min="1"
                max="5"
                value={labelsPerRow}
                onChange={(e) => setLabelsPerRow(Math.max(1, Math.min(5, parseInt(e.target.value) || 1)))}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg"
              />
            </div>
            <div className="col-span-2 flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showProductInfo}
                  onChange={(e) => setShowProductInfo(e.target.checked)}
                  className="w-4 h-4 rounded border-border"
                />
                <span className="text-sm text-muted-foreground">Show Product Info</span>
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={generateBarcode}
            className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Regenerate
          </button>
          <button
            onClick={handleDownload}
            className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
          <button
            onClick={handlePrint}
            className="flex-1 py-3 bg-primary text-primary-foreground rounded-lg font-semibold glow-effect flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print Label
          </button>
        </div>

        {/* Print Instructions */}
        <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <h4 className="text-sm font-bold mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-primary" />
            {labelWidthMm}×{labelHeightMm}mm Label Printing ({labelsPerRow} per row)
          </h4>
          <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
            <li>Select your label printer; set paper size to <strong>Custom ${labelWidthMm * labelsPerRow}mm × ${labelHeightMm}mm</strong></li>
            <li>Disable margins and <strong>always set scaling to 100%</strong> (no "fit to page")</li>
            <li>Use 300 DPI or higher for best scanability</li>
            <li>Print one copy first to check alignment</li>
            <li>Adjust "Labels Per Row" if your sticker roll has a different number of stickers</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default BarcodeGenerator
